import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ShieldCheck, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const [designers, setDesigners] = useState([]);
  const [posts, setPosts] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [designersRes, postsRes, clustersRes] = await Promise.all([
          axios.get(`${API}/designers`),
          axios.get(`${API}/blog`),
          axios.get(`${API}/clusters`),
        ]);
        setDesigners(designersRes.data);
        setPosts(postsRes.data);
        setClusters(clustersRes.data?.clusters || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const featuredPost = posts[0];
  const recentPosts = posts.slice(1, 5);

  return (
    <div data-testid="home-page" className="bg-[#FAF9F6]">
      <SEO
        title="Pre-Owned Luxury Jewelry Guide"
        description="Expert insights on authentication, valuation, and collecting pre-owned luxury jewelry from Cartier, Van Cleef & Arpels, Tiffany, Bulgari and the world's most prestigious houses."
        url="/"
        type="website"
      />
      <SchemaOrg type="website" />
      <SchemaOrg type="organization" />
      
      {/* Hero Section - Editorial Magazine Style */}
      <section className="border-b border-[#E5E0D8]" data-testid="hero-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh]">
            
            {/* Left - Text */}
            <div className="flex flex-col justify-center py-16 lg:py-24 lg:pr-16 lg:border-r border-[#E5E0D8]">
              <p className="overline overline-gold mb-6 animate-fade-in">The Art of Refined Luxury</p>
              
              <h1 className="heading-editorial heading-editorial-lg text-[#111] mb-8 animate-fade-in-up">
                Your Guide to <br/>
                <span className="italic">Pre-Owned</span> <br/>
                Luxury Jewelry
              </h1>
              
              <p className="text-[#555] text-lg leading-relaxed mb-10 max-w-md animate-fade-in-up animation-delay-200">
                Expert insights on authentication, valuation, and collecting from the world's most prestigious houses.
              </p>
              
              <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
                <Link to="/blog" className="btn-editorial" data-testid="explore-articles-btn">
                  Explore Articles
                </Link>
                <Link to="/about" className="btn-editorial btn-editorial-inverse" data-testid="meet-experts-btn">
                  Meet Our Experts
                </Link>
              </div>
            </div>
            
            {/* Right - Featured Image */}
            <div className="relative lg:pl-0 overflow-hidden">
              <div className="absolute inset-0 img-zoom">
                <img 
                  src="https://images.pexels.com/photos/34329825/pexels-photo-34329825.jpeg?auto=compress&w=1200"
                  alt="Luxury jewelry editorial"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Overlay text on image */}
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 bg-gradient-to-t from-black/70 to-transparent">
                <p className="overline text-white/70 mb-2">Featured</p>
                <p className="font-heading text-2xl lg:text-3xl text-white font-light">
                  The Enduring Allure of Estate Jewelry
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Designer Marquee */}
      <section className="py-6 border-b border-[#E5E0D8] overflow-hidden">
        <div className="flex items-center gap-12 animate-marquee whitespace-nowrap">
          {[...designers, ...designers].map((designer, i) => (
            <Link 
              key={i} 
              to={`/designers/${designer.slug}`}
              className="marquee-text hover:text-[#C69C6D] transition-colors duration-300"
            >
              {designer.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Latest Articles - Editorial Grid */}
      <section className="section-editorial" data-testid="articles-section">
        <div className="container-editorial">
          {/* Section Header */}
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">From The Editors</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Latest Articles</h2>
            </div>
            <Link to="/blog" className="hidden md:flex items-center gap-2 text-sm tracking-wide hover:text-[#C69C6D] transition-colors">
              View All <ArrowRight className="w-4 h-4" strokeWidth={1} />
            </Link>
          </div>

          {/* Editorial Grid - Asymmetric */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#E5E0D8]">
            
            {/* Featured Article - Large */}
            {featuredPost && (
              <Link 
                to={`/blog/${featuredPost.slug}`}
                className="lg:col-span-7 bg-[#FAF9F6] group"
                data-testid="featured-article"
              >
                <div className="aspect-[4/3] overflow-hidden img-zoom">
                  <img 
                    src={featuredPost.image_url}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-12">
                  <p className="overline overline-gold mb-4">{featuredPost.category || 'Editorial'}</p>
                  <h3 className="font-heading text-2xl lg:text-3xl text-[#111] mb-4 group-hover:text-[#C69C6D] transition-colors duration-300">
                    {featuredPost.title}
                  </h3>
                  <p className="text-[#555] leading-relaxed mb-6 line-clamp-2">
                    {featuredPost.excerpt}
                  </p>
                  <p className="text-sm text-[#555]">
                    By <span className="text-[#111]">{featuredPost.author_name}</span>
                  </p>
                </div>
              </Link>
            )}

            {/* Recent Articles - Stacked */}
            <div className="lg:col-span-5 flex flex-col">
              {recentPosts.map((post, i) => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`bg-[#FAF9F6] p-6 lg:p-8 flex gap-6 group ${i < recentPosts.length - 1 ? 'border-b border-[#E5E0D8]' : ''}`}
                  data-testid={`recent-article-${i}`}
                >
                  <div className="w-24 h-24 lg:w-32 lg:h-32 flex-shrink-0 overflow-hidden img-zoom">
                    <img 
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="overline text-[10px] mb-2">{post.category || 'Guide'}</p>
                    <h4 className="font-heading text-lg lg:text-xl text-[#111] mb-2 group-hover:text-[#C69C6D] transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h4>
                    <p className="text-sm text-[#555]">{post.author_name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile View All */}
          <div className="mt-8 md:hidden text-center">
            <Link to="/blog" className="btn-editorial">
              View All Articles
            </Link>
          </div>
        </div>
      </section>

      {/* Designer Spotlights - Magazine Grid */}
      <section className="section-editorial bg-[#111] text-white" data-testid="designers-section">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-white/20">
            <div>
              <p className="overline text-[#C69C6D] mb-3">The Houses</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl text-white">Designer Guides</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10">
            {designers.slice(0, 10).map((designer) => (
              <Link 
                key={designer.id}
                to={`/designers/${designer.slug}`}
                className="bg-[#111] p-6 lg:p-8 text-center group hover:bg-[#1a1a1a] transition-colors duration-300"
                data-testid={`designer-${designer.slug}`}
              >
                <div className="aspect-square mb-4 overflow-hidden img-zoom opacity-80 group-hover:opacity-100 transition-opacity">
                  <img 
                    src={designer.image_url}
                    alt={designer.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-lg lg:text-xl text-white group-hover:text-[#C69C6D] transition-colors">
                  {designer.name}
                </h3>
                <p className="text-xs text-white/50 mt-2">Est. {designer.foundation_year}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About / Trust Section */}
      <section className="section-editorial" data-testid="trust-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            
            {/* Image */}
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden img-zoom">
                <img 
                  src="https://images.pexels.com/photos/30844414/pexels-photo-30844414.jpeg?auto=compress&w=800"
                  alt="Editorial jewelry portrait"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 lg:-right-12 bg-[#111] text-white p-6 lg:p-8">
                <p className="font-heading text-4xl lg:text-5xl font-light">10</p>
                <p className="overline text-white/70 mt-2">Expert Writers</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="overline overline-gold mb-6">Why Trust Us</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl mb-8">
                Expertise You <br/>Can <span className="italic">Rely</span> On
              </h2>
              <p className="text-[#555] text-lg leading-relaxed mb-8">
                Our team of GIA-certified gemologists, former auction house specialists, and luxury brand experts 
                bring decades of hands-on experience to every article we publish.
              </p>
              
              <div className="space-y-4 mb-10">
                {[
                  'GIA-Certified Gemologists',
                  'Former Auction House Specialists',
                  'Brand Authentication Experts',
                  'Market Analysts & Appraisers'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-[#C69C6D]" />
                    <span className="text-[#111]">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" className="btn-editorial">
                Meet The Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Topic Clusters — discovery grid for SEO depth */}
      <section className="section-editorial border-t border-[#E5E0D8]" data-testid="home-clusters-section">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Archive</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">
                Sixteen <span className="italic">Topic</span> Clusters
              </h2>
              <p className="text-[#555] mt-4 max-w-2xl">
                Every article belongs to a deeply-researched pillar — from Cartier and Van Cleef to Pearls, Gemstones, and Pre-Owned Watches.
              </p>
            </div>
            <Link to="/methodology" className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase hover:text-[#C69C6D] transition-colors">
              How We Verify <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#E5E0D8] border border-[#E5E0D8]">
            {clusters.map((c) => (
              <Link
                key={c.slug}
                to={`/clusters/${c.slug}`}
                data-testid={`home-cluster-${c.slug}`}
                className="group bg-[#FAF9F6] p-5 lg:p-6 hover:bg-white transition-colors"
              >
                <p className="font-heading text-lg lg:text-xl text-[#111] group-hover:text-[#C69C6D] transition-colors mb-2 leading-tight">
                  {c.name}
                </p>
                {c.article_count > 0 && (
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#888]">
                    {c.article_count} {c.article_count === 1 ? 'story' : 'stories'}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Moat Trio Teaser — Investments / Drops / Celebrity */}
      <section className="section-editorial bg-white border-t border-[#E5E0D8]" data-testid="moat-trio-section">        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">Editor's Tools</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">
                Beyond the <span className="italic">Articles</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E0D8]">
            {/* Investment Index */}
            <Link
              to="/investments"
              className="group relative bg-[#FAF9F6] p-8 lg:p-10 min-h-[340px] flex flex-col justify-between hover:bg-white transition-colors"
              data-testid="home-investments-card"
            >
              <div>
                <p className="overline overline-gold mb-4">Price Tracker</p>
                <h3 className="font-heading text-3xl lg:text-4xl font-light text-[#111] mb-4 group-hover:text-[#C69C6D] transition-colors">
                  The Investment <span className="italic">Index</span>
                </h3>
                <p className="text-[#555] leading-relaxed">
                  Ten-year resale data for 12 iconic pieces. Which Cartiers, VCAs, and Tiffanys actually hold value — and which quietly bleed it.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1 w-fit">
                Open the Index <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>

            {/* Drop Tracker */}
            <Link
              to="/drops"
              className="group relative bg-[#111] text-white p-8 lg:p-10 min-h-[340px] flex flex-col justify-between hover:bg-[#1a1a1a] transition-colors"
              data-testid="home-drops-card"
            >
              <div>
                <p className="overline text-[#C69C6D] mb-4">Fresh Inventory</p>
                <h3 className="font-heading text-3xl lg:text-4xl font-light mb-4 group-hover:text-[#C69C6D] transition-colors">
                  The Drop <span className="italic">Tracker</span>
                </h3>
                <p className="text-white/75 leading-relaxed">
                  Curated pieces live right now on Fashionphile, The RealReal, 1stDibs, and Rebag — with our call on which ones are worth it.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-white border-b border-[#C69C6D] pb-1 w-fit">
                See This Week's Drops <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>

            {/* Celebrity */}
            <Link
              to="/celebrity-jewelry"
              className="group relative bg-[#FAF9F6] p-8 lg:p-10 min-h-[340px] flex flex-col justify-between hover:bg-white transition-colors"
              data-testid="home-celebrity-card"
            >
              <div>
                <p className="overline overline-gold mb-4">Red Carpet · Royal</p>
                <h3 className="font-heading text-3xl lg:text-4xl font-light text-[#111] mb-4 group-hover:text-[#C69C6D] transition-colors">
                  Celebrity <span className="italic">Jewelry</span>
                </h3>
                <p className="text-[#555] leading-relaxed">
                  Taylor Swift's Cartier Love, Zendaya's Bulgari Serpenti, Kate Middleton's sapphire — decoded, sourced, shoppable.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1 w-fit">
                Enter the Archive <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Free Tools CTA Strip — Backlink Magnet */}
      <section className="bg-[#FAF9F6] border-t border-[#E5E0D8]" data-testid="home-tools-section">
        <div className="container-editorial py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14 pb-8 border-b border-[#E5E0D8]">
            <div className="lg:col-span-7">
              <p className="overline overline-gold mb-3">Free Editorial Tools</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">
                Decide with <span className="italic">confidence</span>.
              </h2>
            </div>
            <p className="lg:col-span-5 text-[#555] leading-relaxed text-lg">
              Two decision tools built by our GIA-credentialed editors. No signup, no email gate, no sales pitch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E0D8]">
            {/* Cartier Authenticator */}
            <Link
              to="/tools/authenticate/cartier-love"
              className="group relative bg-white p-8 lg:p-12 min-h-[360px] flex flex-col justify-between hover:bg-[#FAF9F6] transition-colors"
              data-testid="home-authenticator-card"
            >
              <div>
                <ShieldCheck className="w-8 h-8 text-[#C69C6D] mb-6" strokeWidth={1.25} />
                <p className="overline overline-gold mb-4">Authentication · 60 Seconds</p>
                <h3 className="font-heading text-3xl lg:text-4xl font-light text-[#111] mb-4 leading-tight group-hover:text-[#C69C6D] transition-colors">
                  Cartier Love <span className="italic">Authenticator</span>
                </h3>
                <p className="text-[#555] leading-relaxed">
                  Eight expert questions, weighted scoring, decisive verdict. Catches 95% of common counterfeits before you wire funds.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1 w-fit">
                Start the Quiz <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>

            {/* ROI Calculator */}
            <Link
              to="/tools/roi-calculator"
              className="group relative bg-white p-8 lg:p-12 min-h-[360px] flex flex-col justify-between hover:bg-[#FAF9F6] transition-colors"
              data-testid="home-roi-card"
            >
              <div>
                <TrendingUp className="w-8 h-8 text-[#C69C6D] mb-6" strokeWidth={1.25} />
                <p className="overline overline-gold mb-4">Investment · Real Data</p>
                <h3 className="font-heading text-3xl lg:text-4xl font-light text-[#111] mb-4 leading-tight group-hover:text-[#C69C6D] transition-colors">
                  Pre-Owned ROI <span className="italic">Calculator</span>
                </h3>
                <p className="text-[#555] leading-relaxed">
                  Enter what you paid, the year you bought it, and the piece. We benchmark your return against our live Investment Index.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1 w-fit">
                Calculate My Return <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>
          </div>

          <div className="mt-10 flex justify-end">
            <Link
              to="/tools"
              className="text-xs tracking-[0.2em] uppercase text-[#555] hover:text-[#111] transition-colors inline-flex items-center gap-2"
              data-testid="home-tools-index-link"
            >
              See all editorial tools <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
      <section className="py-20 lg:py-28 bg-[#111]" data-testid="newsletter-section">
        <div className="container-editorial text-center">
          <p className="overline text-[#C69C6D] mb-6">Stay Informed</p>
          <h2 className="heading-editorial text-3xl lg:text-4xl text-white mb-4">
            Join Our <span className="italic">Inner Circle</span>
          </h2>
          <p className="text-white/60 mb-10 max-w-md mx-auto">
            Weekly insights on authentication, market trends, and exclusive finds.
          </p>
          
          <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input 
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 bg-transparent border border-white/30 text-white placeholder-white/50 focus:border-[#C69C6D] transition-colors"
              data-testid="newsletter-email"
            />
            <button type="submit" className="btn-editorial bg-white text-[#111] border-white hover:bg-[#C69C6D] hover:border-[#C69C6D] hover:text-white">
              Subscribe
            </button>
          </form>
        </div>
      </section>

    </div>
  );
};

export default Home;
